import React, { useState } from 'react';
import { CategoryResult, SEVERITY_ORDER, CATEGORY_LABELS, Severity, SEVERITY_CLASSES } from '../types';
import { PANEL, PANEL_BADGE, PANEL_HEADER, PANEL_TITLE, severityWord } from './panelChrome';
import { buttonClass } from '../../components/ui/Button';

interface FindingsPanelProps {
  categories: Record<string, CategoryResult>;
  /** Show only this category's findings (a status tile was pressed). */
  focusCategory?: string | null;
  onClearFocus?: () => void;
}

interface GroupedFinding {
  severity: Severity;
  category: string;
  finding: string;
}

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ease-emphasized motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}
  >
    <path fill="currentColor" d="M6 3.5 10.5 8 6 12.5z" />
  </svg>
);

const FindingsPanel: React.FC<FindingsPanelProps> = ({ categories, focusCategory, onClearFocus }) => {
  const [expandedSev, setExpandedSev] = useState<Set<string>>(new Set(['critical', 'warning']));

  const allFindings: GroupedFinding[] = [];
  Object.entries(categories).forEach(([catKey, result]) => {
    if (focusCategory && catKey !== focusCategory) return;
    result.findings?.forEach(f => {
      allFindings.push({ severity: result.severity as Severity, category: catKey, finding: f });
    });
  });
  const focusLabel = focusCategory ? (CATEGORY_LABELS[focusCategory]?.label || focusCategory) : null;

  const grouped: Record<Severity, GroupedFinding[]> = { critical: [], warning: [], advisory: [], info: [] };
  allFindings.forEach(f => {
    if (grouped[f.severity]) grouped[f.severity].push(f);
  });

  const toggle = (sev: string) => {
    setExpandedSev(prev => {
      const next = new Set(prev);
      next.has(sev) ? next.delete(sev) : next.add(sev);
      return next;
    });
  };

  const reversedOrder = [...SEVERITY_ORDER].reverse();

  return (
    <section className={PANEL}>
      <div className={PANEL_HEADER}>
        <h3 className={PANEL_TITLE}>{focusLabel ? `Findings: ${focusLabel}` : 'Findings'}</h3>
        <span className="flex min-w-0 items-center gap-2">
          {focusLabel && onClearFocus && (
            <button
              type="button"
              onClick={onClearFocus}
              className={buttonClass('quiet', 'sm')}
            >
              Show all
            </button>
          )}
          <span className={PANEL_BADGE}>{allFindings.length} findings</span>
        </span>
      </div>
      <div className="flex flex-col gap-1.5 p-3">
        {allFindings.length === 0 && (
          <p className="text-sm text-on-surface-variant">{focusLabel ? `No findings for ${focusLabel}.` : 'No findings in this flight.'}</p>
        )}
        {reversedOrder.map(sev => {
          const items = grouped[sev];
          if (items.length === 0) return null;
          const classes = SEVERITY_CLASSES[sev];
          const isOpen = !!focusCategory || expandedSev.has(sev);
          const listId = `findings-${sev}`;

          return (
            <div key={sev} className="min-w-0">
              <button
                type="button"
                onClick={() => toggle(sev)}
                aria-expanded={isOpen}
                aria-controls={listId}
                className={[
                  'np-state flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-sm font-semibold',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                  classes.chip,
                ].join(' ')}
              >
                <Chevron open={isOpen} />
                {severityWord(sev)}
                <span className="ml-auto tabular-nums">{items.length}</span>
              </button>
              {isOpen && (
                <ul id={listId} className="flex flex-col gap-1.5 py-2 pl-7 pr-1">
                  {items.map((item, i) => {
                    const catLabel = CATEGORY_LABELS[item.category]?.label || item.category;
                    return (
                      <li key={i} className="flex min-w-0 items-start gap-3">
                        <span className="w-20 shrink-0 truncate pt-0.5 text-xs text-on-surface-variant">
                          {catLabel}
                        </span>
                        <span className="min-w-0 break-words text-sm leading-snug text-on-surface">
                          {item.finding}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FindingsPanel;
